/*
 * 0.87: Kalenderbaustein der Übersicht – Woche, Arbeitswoche, zwei Wochen oder Monat; Streifen oder Flächen
 * (Zeitraster mit Uhrzeit). Umplanen per Ziehen über den normalen Kartenweg (PATCH mit Version), nur mit
 * Bearbeitungsrecht am Board. Ohne Ziehen: Karte öffnen und dort den Termin ändern. Schmale Breite → Tagesliste.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { DndContext, DragOverlay, MouseSensor, pointerWithin, TouchSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import {
  calendarEvents,
  calendarPeriod,
  dueState,
  layoutDay,
  layoutWeek,
  localDateKey,
  PRIORITIES,
  rescheduleEvent,
  shiftPeriod,
  type CalCard,
  type CalendarPlanningData,
  type CalPatch,
  type CardSummary,
  type DashFilter,
  type Widget,
  type WidgetData,
} from '@kaba/shared';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { calendarEventKey, planningCalendarEvents, type OverviewEvent } from './calendarPlanning';
import { boardKey, cardKey } from '../../lib/board';
import { navigate } from '../../lib/router';
import { toast } from '../../lib/toast';
import { HolidayToggle, useHolidayDisplay } from '../HolidaySelect';
import { Button, cx, IconButton } from '../ui/ui';
import { catColor, LABEL_HEX } from './charts';
import { SCROLL_KEY } from './widgets';

type CalW = Extract<Widget, { type: 'calendar' }>;
type CalData = Extract<WidgetData, { type: 'calendar' }>;
type Ev = OverviewEvent;

const WD = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const timeFmt = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
});
const monthFmt = new Intl.DateTimeFormat('de-DE', {
  month: 'long',
  year: 'numeric',
});
const dayFmt = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
const shortFmt = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric',
  month: 'short',
});
const asDate = (k: string) => {
  const [y, m, d] = k.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
};
/** Kalenderwoche nach ISO 8601 */
export function isoWeek(k: string): number {
  const d = new Date(`${k}T12:00:00Z`);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((d.getTime() - firstThu.getTime()) / 86_400_000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
}
const HOUR_PX = 44;

function openCard(c: Pick<CalCard, 'id' | 'boardId'>) {
  try {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
  } catch {
    /* egal */
  }
  navigate(`/b/${c.boardId}?card=${c.id}`);
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return;
    setW(ref.current.getBoundingClientRect().width);
    const ro = new ResizeObserver(([e]) => setW(e!.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

const PRIO_COLOR: Record<number, string> = {
  4: 'var(--cat-8)',
  3: 'var(--cat-2)',
  2: 'var(--cat-4)',
  1: 'var(--cat-1)',
  0: 'var(--cat-other)',
};

export function CalendarBody({ w, data: initial, filter }: { w: CalW; data: CalData; filter: DashFilter }) {
  const qc = useQueryClient();
  const me = useMe().data;
  const cfg = w.config;
  const [anchor, setAnchor] = useState<string | null>(null);
  // Einstellungen geändert → wieder beim heutigen Zeitraum beginnen
  const cfgKey = JSON.stringify(cfg);
  useEffect(() => setAnchor(null), [cfgKey]);
  const other = useQuery({
    queryKey: ['dash-data', 'cal', w.id, filter, w.filter, cfg, anchor],
    queryFn: () =>
      api.post<CalData>('/api/dashboard-data/calendar', {
        filter,
        widget: w,
        anchor,
      }),
    enabled: !!anchor,
    placeholderData: keepPreviousData,
  });
  const base = anchor ? (other.data ?? initial) : initial;
  // Sofort sichtbares Umplanen, bis der neue Stand kommt
  const [over, setOver] = useState<Record<string, CalCard>>({});
  useEffect(() => {
    setOver((o) => {
      const next = { ...o };
      for (const c of base.cards) if (next[c.id] && c.version >= next[c.id]!.version) delete next[c.id];
      return next;
    });
  }, [base]);
  const cards = useMemo(() => base.cards.map((c) => over[c.id] ?? c), [base.cards, over]);
  const curAnchor = anchor ?? initial.anchor;
  const p = calendarPeriod(cfg.range, curAnchor);
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const planning = useQuery({
    queryKey: ['planning', me?.id, 'overview', w.id, filter, w.filter, cfg, curAnchor, zone],
    queryFn: () => api.post<CalendarPlanningData>('/api/me/planning/overview', { filter, widget: w, anchor: curAnchor, zone }),
    enabled: !!me && !me.isGuest, gcTime: 0, staleTime: 0, refetchInterval: 30000,
  });
  const personal = planning.isError ? undefined : planning.data;
  const events = useMemo(() => [...calendarEvents(cards, cfg.dates, p.from, p.to), ...planningCalendarEvents(personal, p.days, zone)]
    .sort((a,b) => a.startDay.localeCompare(b.startDay) || (a.startTime ?? '').localeCompare(b.startTime ?? '')),
    [cards, cfg.dates, p.from, p.to, personal, zone]);
  const boards = useMemo(() => new Map([...new Map([...base.boards, ...personal?.boards ?? []].map(b => [b.id,b])).values()]
    .sort((a,b) => a.title.localeCompare(b.title,'de')).map((b,i) => [b.id,{...b,i}])), [base.boards, personal]);
  const labels = useMemo(() => new Map([...base.labels, ...personal?.labels ?? []].map((l) => [l.id, l])), [base.labels, personal]);

  const years = [...new Set([p.from.slice(0, 4), p.to.slice(0, 4)].map(Number))];
  const hol = useHolidayDisplay('dashboardHolidays', years);
  const [ref, width] = useWidth<HTMLDivElement>();
  const narrow = width > 0 && width < 520;
  const grid = !narrow && cfg.look === 'blocks' && (cfg.range === 'week' || cfg.range === 'workweek');

  const today = localDateKey(new Date());
  const title =
    cfg.range === 'month'
      ? monthFmt.format(asDate(`${p.month}-01`))
      : `KW ${isoWeek(p.from)}${cfg.range === 'twoWeeks' ? `–${isoWeek(p.to)}` : ''} · ${shortFmt.format(asDate(p.from))} – ${shortFmt.format(asDate(p.to))}`;

  /* ---------- Umplanen ---------- */
  const [dragging, setDragging] = useState<Ev | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 260, tolerance: 8 },
    }),
  );
  const canEdit = (c: CalCard) => !!boards.get(c.boardId)?.canEdit;

  const send = async (card: CalCard, patch: CalPatch, undo?: CalPatch) => {
    const optimistic = { ...card, ...patch, version: card.version + 1 };
    setOver((o) => ({ ...o, [card.id]: optimistic }));
    try {
      const res = await api.patch<CardSummary>(`/api/cards/${card.id}`, {
        ...patch,
        expectedVersion: card.version,
      });
      const saved = { ...optimistic, ...pickDates(res), version: res.version };
      setOver((o) => ({ ...o, [card.id]: saved }));
      void qc.invalidateQueries({ queryKey: ['dash-data'] });
      void qc.invalidateQueries({ queryKey: boardKey(card.boardId) });
      void qc.invalidateQueries({ queryKey: cardKey(card.id) });
      if (undo)
        toast.info(`„${card.title}“ umgeplant`, {
          label: 'Rückgängig',
          run: () => void send(saved, undo),
        });
    } catch (e) {
      setOver((o) => {
        const { [card.id]: _x, ...rest } = o;
        return rest;
      });
      const status = (e as { status?: number }).status;
      toast.error(status === 409 ? `„${card.title}“ wurde inzwischen geändert – bitte erneut ziehen.` : `${card.title}: ${(e as Error).message}`);
      void qc.invalidateQueries({ queryKey: ['dash-data'] });
    }
  };

  const onDragEnd = ({ active, over: target }: DragEndEvent) => {
    setDragging(null);
    if (!target) return;
    const ev = active.data.current?.ev as Ev | undefined;
    if (!ev || ev.planning) return;
    const id = String(target.id);
    const [kind, day, time] = id.split('|');
    if (kind !== 'day' && kind !== 'slot') return;
    const patch = rescheduleEvent(ev.card, ev.kind, {
      day: day!,
      time: kind === 'slot' ? time : null,
    });
    if (!patch) return;
    const undo: CalPatch = {};
    if ('dueAt' in patch)
      Object.assign(undo, {
        dueAt: ev.card.dueAt,
        dueHasTime: ev.card.dueHasTime,
      });
    if ('startAt' in patch)
      Object.assign(undo, {
        startAt: ev.card.startAt,
        startHasTime: ev.card.startHasTime,
      });
    void send(ev.card, patch, undo);
  };

  const look = { cfg, boards, labels, people: { ...base.people, ...personal?.people }, canEdit };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={({ active }) => {
        setDragging((active.data.current?.ev as Ev) ?? null);
        if ('vibrate' in navigator) navigator.vibrate?.(8);
      }}
      onDragEnd={onDragEnd}
      onDragCancel={() => setDragging(null)}
      accessibility={{
        screenReaderInstructions: {
          draggable: 'Zum Ändern des Termins die Karte öffnen und ihn dort eintragen, oder mit der Maus bzw. per langem Tippen auf einen anderen Tag ziehen.',
        },
      }}
    >
      <div ref={ref} className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1">
          <IconButton label="Vorheriger Zeitraum" onClick={() => setAnchor(shiftPeriod(cfg.range, curAnchor, -1))}>
            <ChevronLeft size={18} />
          </IconButton>
          <h4 className="min-w-0 text-sm font-semibold capitalize" aria-live="polite">
            {title}
          </h4>
          <IconButton label="Nächster Zeitraum" onClick={() => setAnchor(shiftPeriod(cfg.range, curAnchor, 1))}>
            <ChevronRight size={18} />
          </IconButton>
          {anchor && (
            <Button size="sm" onClick={() => setAnchor(null)}>
              Heute
            </Button>
          )}
          <span className="flex-1" />
          <HolidayToggle display={hol} />
        </div>
        {other.isError && (
          <p role="alert" className="text-sm text-rose-700 dark:text-rose-300">
            {(other.error as Error).message}
          </p>
        )}
        {planning.isError && <p role="alert" className="text-sm text-rose-700 dark:text-rose-300">
          Deine eingeplanten Aufgaben konnten nicht geladen werden. <button className="underline" onClick={() => void planning.refetch()}>Erneut versuchen</button>
        </p>}
        {narrow ? (
          <DayList days={p.days} events={events} today={today} holidays={hol.holidays} look={look} />
        ) : grid ? (
          <TimeGrid days={p.days} events={events} today={today} holidays={hol.holidays} look={look} />
        ) : (
          <Weeks days={p.days} month={p.month} cols={cfg.range === 'workweek' ? 5 : 7} events={events} today={today} holidays={hol.holidays} look={look} />
        )}
        <Legend look={look} events={events} />
        {!!personal?.blocks.length && <p className="text-xs text-[var(--ink-soft)]">Meine eingeplante Zeit · nur für dich sichtbar. Fristen bleiben getrennt; Zeitblöcke bearbeitest du an der Karte oder in der Tagesplanung.</p>}
        {!!personal?.more && <p className="text-xs text-amber-800 dark:text-amber-300">{personal.more} weitere Zeitblöcke – Filter enger stellen.</p>}
        {base.more > 0 && <p className="text-xs text-amber-800 dark:text-amber-300">{base.more} weitere Karten im Zeitraum werden nicht gezeigt – Filter enger stellen.</p>}
      </div>
      {createPortal(
        <DragOverlay dropAnimation={null}>
          {dragging ? <div className="w-44 rotate-2 rounded-lg bg-[var(--panel)] px-2 py-1.5 text-sm shadow-2xl ring-2 ring-accent/50">{dragging.card.title}</div> : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

const pickDates = (c: CardSummary) => ({
  startAt: c.startAt,
  startHasTime: c.startHasTime,
  dueAt: c.dueAt,
  dueHasTime: c.dueHasTime,
  completedAt: c.completedAt,
});

interface Look {
  cfg: CalW['config'];
  boards: Map<string, { id: string; title: string; canEdit: boolean; i: number }>;
  labels: Map<string, { id: string; name: string; color: string }>;
  people: Record<string, string>;
  canEdit: (c: CalCard) => boolean;
}

/** Farbe eines Eintrags: Fristlage als Fläche, sonst Streifen links in der Kategorie-/Labelfarbe */
function tone(e: Ev, look: Look): { cls: string; bar?: string } {
  if (e.planning) return { cls: 'bg-teal-600/15 text-teal-950 dark:bg-teal-400/20 dark:text-teal-100', bar: '#0d9488' };
  const c = e.card;
  switch (look.cfg.color) {
    case 'status': {
      if (c.completedAt)
        return {
          cls: 'bg-emerald-600/15 text-[var(--ink)] line-through decoration-1 opacity-75',
        };
      const st = c.dueAt ? dueState(c.dueAt, c.dueHasTime, null) : 'none';
      if (st === 'overdue') return { cls: 'bg-rose-600 text-white' };
      if (st === 'soon') return { cls: 'bg-amber-300 text-amber-950' };
      return { cls: 'bg-accent-soft text-[var(--ink)]' };
    }
    case 'board':
      return {
        cls: 'bg-black/[0.04] dark:bg-white/[0.06]',
        bar: catColor(look.boards.get(c.boardId)?.i ?? 8),
      };
    case 'label': {
      const l = c.labelIds.map((id) => look.labels.get(id)).find(Boolean);
      return {
        cls: 'bg-black/[0.04] dark:bg-white/[0.06]',
        bar: l ? (LABEL_HEX[l.color] ?? 'var(--cat-other)') : 'transparent',
      };
    }
    case 'priority':
      return {
        cls: 'bg-black/[0.04] dark:bg-white/[0.06]',
        bar: PRIO_COLOR[c.priority] ?? 'var(--cat-other)',
      };
    default:
      return { cls: 'bg-accent-soft text-[var(--ink)]' };
  }
}

const kindText = (e: Ev) => e.planning ? (e.planning.state === 'performed' ? 'Durchgeführt' : 'Eingeplant') : (e.kind === 'start' ? 'Start' : e.kind === 'due' ? 'Frist' : 'Zeitraum');
function timeText(e: Ev): string | null {
  if (!e.startTime) return null;
  return e.kind === 'span' && e.endTime && e.timed ? `${timeFmt.format(new Date(e.startTime))}–${timeFmt.format(new Date(e.endTime))}` : timeFmt.format(new Date(e.startTime));
}

/** Einträge als Streifen (eine Zeile) oder Kachel (mehrere Zeilen) – Klick öffnet die Karte, Ziehen plant um */
function Item({ e, look, big, style, className, fill, withRange }: { e: Ev; look: Look; big?: boolean; style?: React.CSSProperties; className?: string; fill?: boolean; withRange?: boolean }) {
  const editable = !e.planning && look.canEdit(e.card);
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: `ev|${calendarEventKey(e)}|${e.startDay}`,
    data: { ev: e },
    disabled: !editable,
  });
  const t = tone(e, look);
  const show = new Set(look.cfg.show);
  const c = e.card;
  const tt = e.planning || show.has('time') ? timeText(e) : null;
  // Mehrtägig (in der Tagesliste nur einmal gezeigt): Zeitraum dazuschreiben
  const range = withRange && e.startDay !== e.endDay ? `${shortFmt.format(asDate(e.startDay))} – ${shortFmt.format(asDate(e.endDay))}` : null;
  const when = [range, tt].filter(Boolean).join(' · ');
  const board = show.has('board') ? c.boardTitle : null;
  const col = show.has('column') ? c.columnTitle : null;
  const people = show.has('assignees') && c.assigneeIds.length ? c.assigneeIds.map((id) => look.people[id] ?? 'Ehemaliges Mitglied').join(', ') : null;
  const labs = show.has('labels') ? c.labelIds.map((id) => look.labels.get(id)).filter((l): l is { id: string; name: string; color: string } => !!l) : [];
  const prio = show.has('priority') && c.priority ? PRIORITIES.find((x) => x.value === c.priority)?.label : null;
  const desc = [
    kindText(e),
    tt ?? (e.startDay !== e.endDay ? `${shortFmt.format(asDate(e.startDay))} – ${shortFmt.format(asDate(e.endDay))}` : 'ganztägig'),
    c.boardTitle,
    c.completedAt ? 'erledigt' : null,
    e.planning ? 'meine eingeplante Zeit, nur für mich sichtbar' : editable ? null : 'nur lesen',
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <button
      ref={setNodeRef}
      type="button"
      {...(editable ? attributes : {})}
      {...(editable ? listeners : {})}
      onClick={() => openCard(c)}
      onContextMenu={(ev) => matchMedia('(pointer: coarse)').matches && ev.preventDefault()}
      aria-label={`${c.title} – ${desc}`}
      title={`${c.title}\n${desc}`}
      data-card-id={c.id}
      data-plan-block-id={e.planning?.id}
      style={{
        ...style,
        ...(t.bar ? { boxShadow: `inset 3px 0 0 ${t.bar}` } : {}),
      }}
      className={cx(
        'kaba-card flex w-full min-w-0 rounded-md text-left',
        e.planning && fill ? 'flex-col gap-0 px-1.5 py-0.5 text-[11px] leading-tight' : big ? 'flex-col gap-0.5 px-2 py-1 text-xs' : cx('min-h-6 gap-1 px-1.5 text-[11px] leading-tight sm:text-xs', fill ? 'items-start pt-1' : 'items-center'),
        t.bar && 'pl-2',
        fill && 'h-full overflow-hidden',
        t.cls,
        isDragging && 'opacity-30',
        className,
      )}
    >
      {e.planning && fill ? <>
        <span className="block w-full truncate font-medium">{c.title}</span>
        {big && <span className="block w-full truncate tabular-nums opacity-80">{tt}</span>}
      </> : big ? (
        <>
          {/* Im Zeitraster zuerst die Uhrzeit – sie bleibt auch bei kurzen Flächen sichtbar */}
          {fill && when && <span className="tabular-nums opacity-80">{when}</span>}
          <span className={cx('font-medium hyphens-auto break-words', c.completedAt && 'line-through')}>{c.title}</span>
          {!fill && <span className="text-[10px] opacity-80">{kindText(e)}</span>}
          {!fill && when && <span className="tabular-nums opacity-80">{when}</span>}
          {(board || col) && <span className="truncate opacity-80">{[board, col].filter(Boolean).join(' · ')}</span>}
          {people && <span className="truncate opacity-80">{people}</span>}
          {(labs.length > 0 || prio) && (
            <span className="flex flex-wrap items-center gap-1">
              {labs.map((l) => (
                <span key={l.id} className="inline-flex items-center gap-1 rounded bg-black/5 px-1 dark:bg-white/10">
                  <span aria-hidden className="size-1.5 rounded-full" style={{ background: LABEL_HEX[l.color] }} />
                  {l.name || 'Label'}
                </span>
              ))}
              {prio && <span className="font-medium">{prio}</span>}
            </span>
          )}
        </>
      ) : (
        <>
          {labs[0] && <span aria-hidden className="size-1.5 shrink-0 rounded-full" style={{ background: LABEL_HEX[labs[0].color] }} />}
          {when && !e.planning && <span className="shrink-0 tabular-nums opacity-80">{when}</span>}
          <span className="min-w-0 truncate">{c.title}</span>
          {when && e.planning && <span className="ml-auto shrink-0 tabular-nums opacity-80">{when}</span>}
          {(board || col || people || prio) && <span className="min-w-0 shrink truncate opacity-70">· {[board, col, people, prio].filter(Boolean).join(' · ')}</span>}
        </>
      )}
      {!editable && !e.planning && !big && <Lock size={9} className="ml-auto shrink-0 opacity-50" aria-hidden />}
    </button>
  );
}

/** Ablagefläche eines Tages (ganztägig) */
function DayDrop({ day, className, children, label, style }: { day: string; className?: string; children?: ReactNode; label?: string; style?: React.CSSProperties }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day|${day}` });
  return (
    <div ref={setNodeRef} aria-label={label} style={style} className={cx(className, isOver && 'bg-accent-soft')}>
      {children}
    </div>
  );
}

const single = (e: Ev) => e.startDay === e.endDay;

/** Wochenzeilen (Woche, zwei Wochen, Monat): mehrtägige Einträge als Balken, Einzeltermine je Tag */
function Weeks({ days, month, cols, events, today, holidays, look }: { days: string[]; month: string | null; cols: number; events: Ev[]; today: string; holidays: Map<string, string>; look: Look }) {
  const weeks: string[][] = [];
  for (let i = 0; i < days.length; i += cols) weeks.push(days.slice(i, i + cols));
  const isMonth = !!month;
  const big = look.cfg.look === 'blocks';
  const limit = isMonth ? (big ? 2 : 3) : 50;
  const [open, setOpen] = useState<Set<number>>(new Set());
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--line)]">
      <div
        className="grid border-b border-[var(--line)] bg-[var(--column)] text-center text-xs font-semibold text-[var(--ink-soft)]"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {WD.slice(0, cols).map((d) => (
          <div key={d} className="py-1">
            {d}
          </div>
        ))}
      </div>
      {weeks.map((wk, wi) => {
        const spans = layoutWeek(
          events.filter((e) => !single(e)),
          wk,
        );
        const lanes = spans.reduce((a, s) => Math.max(a, s.lane + 1), 0);
        const expanded = open.has(wi);
        return (
          <div
            key={wk[0]}
            className="relative grid border-b border-[var(--line)] last:border-b-0"
            style={{
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              gridTemplateRows: lanes ? `auto repeat(${lanes}, auto) 1fr` : 'auto 1fr',
            }}
          >
            {/* Ablageflächen als Hintergrund je Tag */}
            {wk.map((d, i) => {
              const hd = holidays.get(d);
              return (
                <DayDrop
                  key={`bg-${d}`}
                  day={d}
                  label={hd ? `${dayFmt.format(asDate(d))}, Feiertag: ${hd}` : dayFmt.format(asDate(d))}
                  className={cx(
                    'border-r border-[var(--line)] last:border-r-0',
                    month && !d.startsWith(month) && 'bg-black/[0.025] dark:bg-white/[0.02]',
                    hd && 'bg-amber-500/[0.08] dark:bg-amber-400/[0.08]',
                  )}
                  style={{ gridColumn: i + 1, gridRow: '1 / -1' }}
                />
              );
            })}
            {wk.map((d, i) => (
              <div key={`h-${d}`} className="pointer-events-none flex items-center gap-1 px-1 pt-1" style={{ gridColumn: i + 1, gridRow: 1 }}>
                {holidays.get(d) && <span className="min-w-0 flex-1 truncate text-[10px] text-amber-800 dark:text-amber-300">{holidays.get(d)}</span>}
                <span
                  className={cx(
                    'ml-auto inline-flex size-6 items-center justify-center rounded-full text-xs',
                    d === today ? 'bg-accent font-semibold text-on-accent' : month && !d.startsWith(month) && 'text-[var(--ink-soft)]',
                  )}
                >
                  {asDate(d).getDate()}
                </span>
              </div>
            ))}
            {spans.map((s) => (
              <div
                key={calendarEventKey(s.e)}
                className="relative z-[1] px-0.5 py-px"
                style={{
                  gridColumn: `${s.col + 1} / span ${s.span}`,
                  gridRow: s.lane + 2,
                }}
              >
                <Item e={s.e} look={look} className={cx(s.cutStart && 'rounded-l-none', s.cutEnd && 'rounded-r-none')} />
              </div>
            ))}
            {wk.map((d, i) => {
              const list = events.filter((e) => single(e) && e.startDay === d);
              const shown = expanded ? list : list.slice(0, limit);
              return (
                <div
                  key={`c-${d}`}
                  className={cx('relative z-[1] flex min-w-0 flex-col gap-0.5 p-0.5 pb-1', isMonth ? 'min-h-16' : big ? 'min-h-40' : 'min-h-28')}
                  style={{ gridColumn: i + 1, gridRow: lanes + 2 }}
                >
                  {shown.map((e) => (
                    <Item key={calendarEventKey(e)} e={e} look={look} big={big} />
                  ))}
                  {list.length > shown.length && (
                    <button type="button" className="min-h-6 rounded px-1 text-left text-[11px] font-medium text-accent-fg hover:underline" onClick={() => setOpen((o) => new Set(o).add(wi))}>
                      + {list.length - shown.length} weitere
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/** Zeitraster (Woche/Arbeitswoche als Flächen): ganztägige oben, Termine mit Uhrzeit an ihrer Stelle */
function TimeGrid({ days, events, today, holidays, look }: { days: string[]; events: Ev[]; today: string; holidays: Map<string, string>; look: Look }) {
  const [h0, h1] = look.cfg.hours;
  const hours = Array.from({ length: h1 - h0 }, (_, i) => h0 + i);
  const allDay = events.filter((e) => !e.timed);
  const spans = layoutWeek(allDay, days);
  const lanes = spans.reduce((a, s) => Math.max(a, s.lane + 1), 0);
  const n = days.length;
  const tpl = `3rem repeat(${n}, minmax(0, 1fr))`;
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--line)]">
      <div className="grid border-b border-[var(--line)] bg-[var(--column)] text-xs" style={{ gridTemplateColumns: tpl }}>
        <div />
        {days.map((d) => (
          <div key={d} className="flex flex-col items-center py-1">
            <span className="font-semibold text-[var(--ink-soft)]">{WD[(asDate(d).getDay() + 6) % 7]}</span>
            <span className={cx('inline-flex size-6 items-center justify-center rounded-full', d === today && 'bg-accent font-semibold text-on-accent')}>{asDate(d).getDate()}</span>
            {holidays.get(d) && <span className="max-w-full truncate px-1 text-[10px] text-amber-800 dark:text-amber-300">{holidays.get(d)}</span>}
          </div>
        ))}
      </div>
      {/* Ganztägig und über mehrere Tage */}
      <div
        className="relative grid border-b border-[var(--line)]"
        style={{
          gridTemplateColumns: tpl,
          gridTemplateRows: `repeat(${Math.max(1, lanes)}, auto)`,
        }}
      >
        <div className="self-center px-1 text-[10px] leading-tight break-words text-[var(--ink-soft)]" style={{ gridColumn: 1, gridRow: '1 / -1' }} title="ganztägig">
          ganz&shy;tägig
        </div>
        {days.map((d, i) => (
          <DayDrop key={d} day={d} className="min-h-7 border-l border-[var(--line)]" label={`${dayFmt.format(asDate(d))}, ganztägig`} style={{ gridColumn: i + 2, gridRow: '1 / -1' }} />
        ))}
        {spans.map((s) => (
          <div
            key={calendarEventKey(s.e)}
            className="relative z-[1] px-0.5 py-px"
            style={{
              gridColumn: `${s.col + 2} / span ${s.span}`,
              gridRow: s.lane + 1,
            }}
          >
            <Item e={s.e} look={look} />
          </div>
        ))}
      </div>
      {/* Stunden */}
      <div className="relative grid max-h-[32rem] overflow-y-auto" style={{ gridTemplateColumns: tpl }}>
        <div>
          {hours.map((h) => (
            <div key={h} className="pr-1 text-right text-[10px] text-[var(--ink-soft)] tabular-nums" style={{ height: HOUR_PX }}>
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>
        {days.map((d) => {
          const items = layoutDay(events.filter((e) => e.timed && e.startDay === d));
          return (
            <div key={d} className={cx('relative border-l border-[var(--line)]', holidays.get(d) && 'bg-amber-500/[0.06]')} style={{ height: hours.length * HOUR_PX }}>
              {hours
                .flatMap((h) => [`${String(h).padStart(2, '0')}:00`, `${String(h).padStart(2, '0')}:30`])
                .map((t, j) => (
                  <Slot key={t} day={d} time={t} top={(j * HOUR_PX) / 2} />
                ))}
              {d === today && nowMin >= h0 * 60 && nowMin <= h1 * 60 && (
                <div aria-hidden className="pointer-events-none absolute inset-x-0 z-[2] h-0.5 bg-rose-500" style={{ top: ((nowMin - h0 * 60) / 60) * HOUR_PX }} />
              )}
              {items.map((it) => {
                const top = Math.max(0, ((it.top - h0 * 60) / 60) * HOUR_PX);
                const bottom = Math.min(hours.length * HOUR_PX, ((it.end - h0 * 60) / 60) * HOUR_PX);
                const outside = it.end <= h0 * 60 || it.top >= h1 * 60;
                return (
                  <div
                    key={calendarEventKey(it.e)}
                    className="absolute z-[1] p-px"
                    style={{
                      top: outside ? (it.top < h0 * 60 ? 0 : hours.length * HOUR_PX - 22) : top,
                      height: outside ? 22 : Math.max(22, bottom - top),
                      left: `${(it.slot / it.slots) * 100}%`,
                      width: `${100 / it.slots}%`,
                    }}
                  >
                    <Item e={it.e} look={look} big={!outside && bottom - top >= (it.e.planning ? 32 : 64)} fill />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Slot({ day, time, top }: { day: string; time: string; top: number }) {
  const { setNodeRef, isOver } = useDroppable({ id: `slot|${day}|${time}` });
  return <div ref={setNodeRef} className={cx('absolute inset-x-0', time.endsWith(':00') && 'border-t border-[var(--chart-grid)]', isOver && 'bg-accent-soft')} style={{ top, height: HOUR_PX / 2 }} />;
}

/** Schmale Breite (Handy, schmaler Baustein): Tage untereinander, nur Tage mit Einträgen und heute */
function DayList({ days, events, today, holidays, look }: { days: string[]; events: Ev[]; today: string; holidays: Map<string, string>; look: Look }) {
  // Mehrtägige Einträge nur einmal: am ersten Tag (oder am ersten Tag des Zeitraums, wenn sie früher beginnen)
  const dayOf = (e: Ev) => (e.startDay < days[0]! ? days[0]! : (days.find((d) => d >= e.startDay) ?? e.startDay));
  const shown = days.filter((d) => d === today || holidays.has(d) || events.some((e) => dayOf(e) === d));
  if (!shown.length)
    return (
      <p className="flex items-center gap-2 py-2 text-sm text-[var(--ink-soft)]">
        <CalendarDays size={16} aria-hidden /> Keine Termine in diesem Zeitraum.
      </p>
    );
  return (
    <div className="flex flex-col gap-2">
      {shown.map((d) => {
        const list = events.filter((e) => dayOf(e) === d);
        return (
          <DayDrop key={d} day={d} className="rounded-xl border border-[var(--line)]">
            <h5 className={cx('rounded-t-xl px-2 py-1 text-xs font-semibold', d === today ? 'bg-accent text-on-accent' : 'bg-[var(--column)]')}>
              {d === today ? `Heute · ${dayFmt.format(asDate(d))}` : dayFmt.format(asDate(d))}
              {holidays.get(d) && <span className="ml-2 font-normal">Feiertag: {holidays.get(d)}</span>}
            </h5>
            <div className="flex flex-col gap-1 p-1">
              {list.length ? (
                list.map((e) => <Item key={calendarEventKey(e)} e={e} look={look} big={look.cfg.look === 'blocks'} withRange />)
              ) : (
                <p className="px-1 text-xs text-[var(--ink-soft)]">Nichts eingetragen.</p>
              )}
            </div>
          </DayDrop>
        );
      })}
    </div>
  );
}

/** Legende für Board-, Label- und Prioritätsfarben (Identität nie nur über Farbe) */
function Legend({ look, events }: { look: Look; events: Ev[] }) {
  const mode = look.cfg.color;
  let items: { key: string; label: string; color: string }[] = [];
  if (mode === 'board') {
    const used = new Set(events.map((e) => e.card.boardId));
    items = [...look.boards.values()].filter((b) => used.has(b.id)).map((b) => ({ key: b.id, label: b.title, color: catColor(b.i) }));
  } else if (mode === 'label') {
    const used = new Set(events.flatMap((e) => e.card.labelIds.slice(0, 1)));
    items = [...look.labels.values()]
      .filter((l) => used.has(l.id))
      .map((l) => ({
        key: l.id,
        label: l.name || 'Label',
        color: LABEL_HEX[l.color] ?? 'var(--cat-other)',
      }));
  } else if (mode === 'priority') {
    const used = new Set(events.map((e) => e.card.priority));
    items = PRIORITIES.filter((p) => used.has(p.value)).map((p) => ({
      key: String(p.value),
      label: p.label,
      color: PRIO_COLOR[p.value]!,
    }));
  } else if (mode === 'status') {
    items = [
      { key: 'o', label: 'Überfällig', color: '#e11d48' },
      { key: 's', label: 'Fällig in 24 Std.', color: '#fcd34d' },
    ];
  }
  if (events.some(e => e.planning)) items.push({ key: 'planning', label: 'Meine eingeplante Zeit', color: '#0d9488' });
  if (!items.length) return null;
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--ink-soft)]" aria-label="Legende">
      {items.map((i) => (
        <li key={i.key} className="flex items-center gap-1">
          <span aria-hidden className="size-2.5 rounded-sm" style={{ background: i.color }} />
          {i.label}
        </li>
      ))}
    </ul>
  );
}
